// Fair rotation between boosted deals: every boosted shop takes a turn at the top.
// The order changes once an hour and is identical for everyone, so it never reshuffles mid-scroll.
export const isBoosted = (d) => !!d.boostedUntil && new Date(d.boostedUntil).getTime() > Date.now();

const hash = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

export const boostRank = (id) => hash(`${id || ""}|${Math.floor(Date.now() / 3600000)}`);

// A boost decides the order between deals that are equally near the shopper — it never
// beats distance itself. Anything closer stays closer.
export const PROXIMITY_BANDS = [2, 5, 15]; // km edges: walkable, short drive, across town, further
export const BOOST_RADIUS_KM = 10; // beyond this a boost stops paying off, so it stops ranking

export const proximityBand = (km) => {
  if (km == null) return PROXIMITY_BANDS.length;
  const i = PROXIMITY_BANDS.findIndex((edge) => km <= edge);
  return i === -1 ? PROXIMITY_BANDS.length : i;
};

// Boosted AND close enough for the shopper to actually go there. A shop that bought Reach ranks
// out to a wider radius; everyone else stops at the standard one.
export const boostCounts = (d, km) =>
  isBoosted(d) && (km == null || km <= (Number(d.boostReachKm) || BOOST_RADIUS_KM));

// Slot pools are drawn around each shop, so overlapping circles can put more than three boosted
// deals in one shopper's range. This is the backstop: a list never shows more than 3 sponsored
// deals, nor more than ~30% of a short list. The rest keep their badge and fall back to their
// normal distance position, and the hourly rotation decides who holds the paid spots this hour.
export const SPONSORED_MAX = 3;
export const SPONSORED_SHARE = 0.3;

export const sponsoredAllowance = (count) =>
  Math.max(1, Math.min(SPONSORED_MAX, Math.round(count * SPONSORED_SHARE)));

export const promotedIds = (list, idOf = (d) => d.dealId || d.id, kmOf = () => null) => {
  const eligible = list
    .filter((d) => boostCounts(d, kmOf(d)))
    .sort((a, b) => (proximityBand(kmOf(a)) - proximityBand(kmOf(b))) || (boostRank(idOf(a)) - boostRank(idOf(b))));
  return new Set(eligible.slice(0, sponsoredAllowance(list.length)).map(idOf));
};

const isPromoted = (d, km, idOf, promoted) =>
  boostCounts(d, km) && (!promoted || promoted.has(idOf(d)));

// Sort: nearest band first, then sponsored first inside that band, then plain distance. When two
// deals are the same distance away and neither is sponsored, a Business+ shop takes it — that is
// the whole of "priority placement": a tie-break, never a way past a closer shop.
export const byProximityBoost = (idOf = (d) => d.dealId || d.id, kmOf = () => null, promoted = null) => (a, b) => {
  const ka = kmOf(a);
  const kb = kmOf(b);
  const band = proximityBand(ka) - proximityBand(kb);
  if (band !== 0) return band;
  const pa = isPromoted(a, ka, idOf, promoted);
  const pb = isPromoted(b, kb, idOf, promoted);
  if (pa !== pb) return pa ? -1 : 1;
  if (pa) return boostRank(idOf(a)) - boostRank(idOf(b));
  const dk = (ka ?? 1e9) - (kb ?? 1e9);
  if (Math.abs(dk) > PLAN_TIE_KM) return dk;
  const plan = (b.planPriority ? 1 : 0) - (a.planPriority ? 1 : 0);
  if (plan !== 0) return plan;
  // Two Business+ shops the same distance away: rotate hourly so neither owns the spot.
  if (a.planPriority && b.planPriority) return boostRank(idOf(a)) - boostRank(idOf(b));
  return dk;
};

// Two deals this close together are "the same distance" to a shopper on foot.
export const PLAN_TIE_KM = 0.05;

// Any plain list of deals (home rows, Ending soon) — sponsored deals ride to the top inside their
// distance band, capped and rotated the same way as Browse and the map.
export const withBoostOrder = (list, kmOf = () => null, idOf = (d) => d.dealId || d.id) => {
  if (!list || list.length < 2) return list || [];
  const promoted = promotedIds(list, idOf, kmOf);
  return [...list].sort(byProximityBoost(idOf, kmOf, promoted));
};

// Featured is the front-page placement. It follows the same rule as a boost: paying lifts you
// within the shopper's distance band, it never drags a far-away shop above a nearby one, and the
// order between featured shops rotates hourly so they share the top of the row.
export const FEATURED_RADIUS_KM = 25;

export const withFeaturedOrder = (list, kmOf = () => null, idOf = (d) => d.id) => {
  if (!list || list.length < 2) return list || [];
  return [...list].sort((a, b) =>
    (proximityBand(kmOf(a)) - proximityBand(kmOf(b))) || (boostRank(idOf(a)) - boostRank(idOf(b))));
};

// Which boosted deal currently owns the lead slot (biggest pin on the map).
export const leadBoostId = (list, idOf = (d) => d.dealId || d.id, kmOf = () => null, promoted = null) => {
  const boosted = list.filter((d) => isPromoted(d, kmOf(d), idOf, promoted));
  if (!boosted.length) return null;
  return boosted.reduce((best, d) => (boostRank(idOf(d)) < boostRank(idOf(best)) ? d : best), boosted[0]);
};
